import React, { useState, useEffect } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getManagementPendingOrders, getManagementOrderById } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError } from '../utils/helpers';
import ManagementOrderDetailsView from './ManagementOrderDetailsView';
import SuccessModal from '../components/common/SuccessModal';
import WholesalerName from '../components/common/WholesalerName';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const MoneyDeposit = () => {
    const { accessToken } = useAuth(); // Always call hooks at the top level
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const navigate = useNavigate();

    // Success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successModalData, setSuccessModalData] = useState({ orderId: '', action: '', status: '' });

    // Fetch orders with status 'APPROVED1' and 'Edited/APPROVED1'
    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Fetch both statuses and merge results
            const [approved1, editedApproved1] = await Promise.all([
                getManagementPendingOrders({ page: 0, size: 50, status: 'APPROVED1' }),
                getManagementPendingOrders({ page: 0, size: 50, status: 'Edited/APPROVED1' })
            ]);
            // Merge and deduplicate by order id
            const allOrders = [...(approved1.content || []), ...(editedApproved1.content || [])];
            const uniqueOrders = Array.from(new Map(allOrders.map(o => [o.id, o])).values());
            setOrders(uniqueOrders);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setError('Failed to fetch deposit orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line
    }, [accessToken]);

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getManagementOrderById(orderId);
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    const handleCloseSuccessModal = () => {
        setShowSuccessModal(false);
        setSuccessModalData({ orderId: '', action: '', status: '' });
    };

    const handleStatusUpdate = (orderId, newStatus, successData = null) => {
        // Remove the updated order from the orders list
        setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));

        // Close the details view
        setSelectedOrder(null);
        setDetailsError(null);

        // Show success modal if success data is provided
        if (successData) {
            setSuccessModalData(successData);
            setShowSuccessModal(true);
        }

        // Show success message (optional)

    };

    if (loading) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="max-w-6xl mx-auto p-6">
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
                        {error}
                    </div>
            </div>
        );
    }

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-4">Deposit Money</h1>
                <p className="text-gray-600 mb-6">This page displays orders with status <b>APPROVED1</b> and <b>Edited/APPROVED1</b> for deposit management.</p>
            </div>
            {selectedOrder ? (
                <div>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                        )
                    ) : (
                        <ManagementOrderDetailsView order={selectedOrder} onOrderUpdate={handleStatusUpdate} />
                    )}
                </div>
            ) : (
                orders.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 text-center">
                        <p className="text-gray-500 text-lg">No deposit orders found</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {orders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{order.id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                <div>{order.assignedDealer || order.dealerName || '-'}</div>
                                                {order.wholesalerId && (
                                                    <WholesalerName wholesalerId={order.wholesalerId} />
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>{order.status}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDateTime(order.createdAt)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <button className="text-blue-600 hover:text-blue-900 mr-3" onClick={() => handleViewDetails(order.id)}>View Details</button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )
            )}
            <SuccessModal
                isOpen={showSuccessModal}
                onClose={handleCloseSuccessModal}
                orderId={successModalData.orderId}
                action={successModalData.action}
                status={successModalData.status}
            />
        </div>
    );
};

export default MoneyDeposit; 
